import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { RouteConfig } from '@app/routing/RenderRoutes'

import { Navigate } from '@lib/react-router-dom'

import { ProductAppropriatenessTestDeniedPage } from '@pages/ProductRegistration/AppropriatenessTest/ProductAppropriatenessTestDenied.page'
import { ProductAppropriatenessTestFailedPage } from '@pages/ProductRegistration/AppropriatenessTest/ProductAppropriatenessTestFailed.page'
import { ProductAppropriatenessTestRiskPage } from '@pages/ProductRegistration/AppropriatenessTest/ProductAppropriatenessTestRisk.page'
import { ProductAppropriatenessTestRiskDeclinedPage } from '@pages/ProductRegistration/AppropriatenessTest/ProductAppropriatenessTestRiskDeclined.page'
import { ProductAppropriatenessTestSuccessPage } from '@pages/ProductRegistration/AppropriatenessTest/ProductAppropriatenessTestSuccess.page'
import { ProductAppTestRiskAcceptancePage } from '@pages/ProductRegistration/EconomicProfile/ProductAppTestRiskAcceptance.page'
import { ProductEconomicProfilePage } from '@pages/ProductRegistration/EconomicProfile/ProductEconomicProfile.page'
import { ProductEconomicProfileSuccessPage } from '@pages/ProductRegistration/EconomicProfile/ProductEconomicProfileSuccess.page'
import { YearlyKycUpdateDetailsSuccessPage } from '@pages/Profile/YearlyKycUpdateDetails/YearlyKycUpdateSuccessPage'
import { useRewardsHubRoutes } from '@pages/RewardsHub/RewardsHub.routes'
import { Deposit3DSCallbackPage } from '@pages/Traders-Room/Deposit/Deposit3DSCallback.page'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useProductReadContext } from '@context/ProductContext'

import { getAccountAccessStatuses } from '@utils/AccountAccess/accountAccessStatuses'
import { hasConsentedToLegalDocuments } from '@utils/AccountAccess/accountDocumentStatuses'
import { isKYCUpdateRequired } from '@utils/AccountAccess/accountKYCStatuses'

import { showClosedAccountPage, showInactiveAccountPage } from '@model/AccountDetailedDto'

import { useLandingPage } from '@hooks/useLandingPage'

import { DepositRedirectFromProviderPage } from '../../PaymentProviderRedirect/DepositRedirectFromProviderPage/DepositRedirectFromProviderPage'
import { useIBRoutes } from '../IntroducingBroker/IntroducingBrokerRoutes'
import { useLearningRoutes } from '../Learning/LearningRoutes'
import { usePARoutes } from '../PaymentAgent/PaymentAgentRoutes'
import { ProductAppropriatenessTestPage } from '../ProductRegistration/AppropriatenessTest/ProductAppropriatenessTest.page'
import { ProductLegalDocumentsPage } from '../ProductRegistration/LegalDocuments/ProductLegalDocumentsPage'
import { ProductPersonalDetailsPage } from '../ProductRegistration/ProductPersonalDetails/ProductPersonalDetailsPage'
import { ProductRegistrationGuard } from '../ProductRegistration/ProductRegistrationGuard'
import { ProductLegalDocumentsStepPage } from '../ProductRegistration/ProductRegistrationStepsPage/ProductLegalDocumentsStepPage'
import { useProfileRoutesConditions } from '../Profile/ProfileRoutes'
import { useSocialTradingRoutes } from '../SocialTrading/SocialTradingRoutes'
import { StatementDailyPage, StatementMonthlyPage } from '../Statements/Statement'
import { StatementsETD } from '../Statements/StatementsETD'
import { useSubscriptionRoutes } from '../Subscriptions/SubscriptionsRoutes'
import { useToolsRoutes } from '../Tools/ToolsRoutes'
import { useTradeRoutes } from '../Trade/Trade.routes'
import {
  useTraderRoomRoutes,
  useTraderRoomRoutesConditions,
} from '../Traders-Room/TradersRoom.routes'
import { ProductAppropriatenessTestRequiredPage } from '../WarningAccount/ProductAppropriatenessTestRequiredPage'
import { WarningAccountPage } from '../WarningAccount/WarningAccountPage'
import { Dashboard } from './Dashboard'

export const useDashboardRoutes = (): RouteConfig[] => {
  const { account } = useAccountReadContext()
  const { product } = useProductReadContext()
  const { isDashboardVisible } = useDashboardRoutesConditions()

  const traderRoomRoutes = useTraderRoomRoutes()
  const { isTradersRoomVisible } = useTraderRoomRoutesConditions()
  const tradeRoutes = useTradeRoutes()
  const ibRoutes = useIBRoutes()
  const paRoutes = usePARoutes()
  const toolsRoutes = useToolsRoutes()
  const learningRoutes = useLearningRoutes()
  const subscriptionsRoutes = useSubscriptionRoutes()
  const socialTradingRoutes = useSocialTradingRoutes()
  const rewardsHubRoutes = useRewardsHubRoutes()

  const { isAccountActivatedStatus } = getAccountAccessStatuses(account, product)
  const { getLandingPage } = useLandingPage()
  const { t } = useTranslation()

  return useMemo(
    (): RouteConfig[] => [
      {
        path: '/deposit/3ds/callback',
        element: <Deposit3DSCallbackPage />,
        layout: 'full-no-menu',
      },
      {
        path: 'payments',
        children: [
          {
            path: 'redirect-to-transactions',
            children: [
              {
                path: ':statusId',
                element: <DepositRedirectFromProviderPage />,
              },
            ],
          },
        ],
      },
      {
        path: 'dashboard',
        outlet: <Dashboard />,
        element: <Navigate to={getLandingPage()} />,
        condition: isDashboardVisible,
        children: [
          {
            path: 'closed-account',
            condition: showClosedAccountPage(account, product),
            element: <WarningAccountPage status={'Closed'} />,
          },
          {
            path: 'closed-account',
            condition: showInactiveAccountPage(account, product),
            element: <WarningAccountPage status={'Inactive'} />,
          },
          {
            path: 'legal-documents',
            condition: !hasConsentedToLegalDocuments(account, product) || isAccountActivatedStatus,
            element: <ProductLegalDocumentsPage />,
          },
          {
            path: 'update-kyc',
            element: <ProductPersonalDetailsPage />,
          },
          ...rewardsHubRoutes,
          ...traderRoomRoutes,
          ...tradeRoutes,
          ...ibRoutes,
          ...paRoutes,
          ...toolsRoutes,
          ...learningRoutes,
          ...subscriptionsRoutes,
          ...socialTradingRoutes,
          {
            path: 'statements',
            outlet: <StatementsETD />,
            condition: isTradersRoomVisible,
            meta: {
              breadcrumbLabel: t('Tabs.Statements'),
            },
            children: [
              {
                path: 'daily',
                element: <StatementDailyPage />,
                meta: {
                  breadcrumbLabel: t('Statements.Daily'),
                },
              },
              {
                path: 'monthly',
                element: <StatementMonthlyPage />,
                meta: {
                  breadcrumbLabel: t('Statements.Monthly'),
                },
              },
            ],
          },
        ],
      },
      {
        path: 'dashboard',
        outlet: <Dashboard />,
        element: <Navigate to={getLandingPage()} />,
        children: [
          {
            path: 'product-registration/:productId?',
            outlet: <ProductRegistrationGuard />,
            element: <ProductAppropriatenessTestRequiredPage />,
            layout: 'minimal-auth-no-container',
            children: [
              {
                path: 'legal-documents',
                layout: 'minimal-auth',
                element: <ProductLegalDocumentsStepPage />,
              },
              {
                path: 'appropriateness-test',
                element: <ProductAppropriatenessTestPage />,
                children: [
                  {
                    path: 'success',
                    element: <ProductAppropriatenessTestSuccessPage />,
                  },
                  {
                    path: 'failed',
                    element: <ProductAppropriatenessTestFailedPage />,
                  },
                  {
                    path: 'denied',
                    element: <ProductAppropriatenessTestDeniedPage />,
                  },
                  {
                    path: 'risk',
                    element: <ProductAppropriatenessTestRiskPage />,
                  },
                  {
                    path: 'risk-declined',
                    element: <ProductAppropriatenessTestRiskDeclinedPage />,
                  },
                ],
              },
              {
                path: 'app-test-risk',
                element: <ProductAppTestRiskAcceptancePage />,
              },
              {
                path: 'economic-profile-test',
                element: <ProductEconomicProfilePage />,
                children: [
                  {
                    path: 'success',
                    element: <ProductEconomicProfileSuccessPage />,
                  },
                ],
              },
            ],
          },
          {
            path: 'kyc-personal-details/success',
            element: <YearlyKycUpdateDetailsSuccessPage />,
          },
          {
            path: 'kyc-personal-details',
            condition: isKYCUpdateRequired(account),
            element: <ProductPersonalDetailsPage />,
          },
        ],
      },
    ],
    [
      getLandingPage,
      isDashboardVisible,
      account,
      product,
      isAccountActivatedStatus,
      rewardsHubRoutes,
      traderRoomRoutes,
      tradeRoutes,
      ibRoutes,
      paRoutes,
      toolsRoutes,
      learningRoutes,
      subscriptionsRoutes,
      socialTradingRoutes,
      isTradersRoomVisible,
      t,
    ]
  )
}

interface RoutesConditions {
  isDashboardVisible: boolean
}

export const useDashboardRoutesConditions = (): RoutesConditions => {
  const { isOnlyKYCUpdateVisible } = useProfileRoutesConditions()

  const isDashboardVisible = useMemo(() => !isOnlyKYCUpdateVisible, [isOnlyKYCUpdateVisible])

  return {
    isDashboardVisible,
  }
}
